import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.museshaircwb.com';

const schemaJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/about#webpage`,
      "url": `${SITE_URL}/about`,
      "name": "About | Muses Hair CWB - Professional Hair Stylist & Scalp Therapist Causeway Bay",
      "description": "Meet the expert behind Muses Hair CWB. 20+ years hair styling experience serving international clients, certified scalp therapist specializing in hair and scalp problem solutions in Hong Kong.",
      "inLanguage": "en",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": SITE_URL
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "About",
            "item": `${SITE_URL}/about`
          }
        ]
      }
    },
    {
      "@type": "Person",
      "@id": `${SITE_URL}/about#person`,
      "name": "Muses Hair CWB Stylist",
      "jobTitle": "Professional Hair Stylist & Certified Scalp Therapist",
      "worksFor": {
        "@type": "HairSalon",
        "@id": `${SITE_URL}/#hairsalon`,
        "name": "Muses Hair CWB"
      },
      "knowsAbout": [
        "Hair Styling",
        "Scalp Therapy",
        "Hair Loss Treatment",
        "Color Correction",
        "Keratin Treatment",
        "International Hair Care"
      ],
      "description": "Expert hair stylist with 20+ years experience and certified scalp therapist serving international clients in Causeway Bay, Hong Kong."
    }
  ]
};

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const scriptId = 'schema-jsonld-about';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schemaJsonLd);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/contact');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#F3F0EB]/98 backdrop-blur-sm shadow-sm' : 'bg-[#F3F0EB]'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/">
              <img 
                src="https://www.museshaircwb.com/wp-content/uploads/2024/01/cropped-Muses-Hair-Logo-1.png" 
                alt="Muses Hair CWB" 
                className="h-11 w-auto cursor-pointer"
              />
            </Link>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Home</Link>
            <Link to="/about" className="text-sm font-medium text-gray-900 transition-colors">About</Link>
            <Link to="/qualifications" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Qualifications</Link>
            <Link to="/services" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Services</Link>
            <Link to="/products" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Products</Link>
            <Link to="/testimonials" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Testimonials</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Contact</Link>
          </div>
          <button onClick={scrollToContact} className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-2.5 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer">
            Book Consultation
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block border border-gray-400 text-gray-700 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider mb-8">
            About Me
          </div>
          <h1 className="font-serif text-5xl md:text-6xl text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Where Artistry Meets<br />Clinical Expertise
          </h1>
          <p className="text-gray-700 text-lg leading-relaxed max-w-2xl mx-auto">
            A unique journey combining two decades of international hair styling mastery with advanced scalp therapy certification — dedicated to solving real hair and scalp problems.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center mb-20">
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/6fc1d41d179235fc7bc403be7272ea33.jpg" 
                  alt="Professional Hair Stylist" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                My Journey:<br />From Stylist to<br />Solution Expert
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                My career in hair began over 20 years ago with a simple passion: helping people feel confident and beautiful. Working with clients from around the world — expats, international professionals, and Hong Kong locals — I quickly learned that beautiful hair isn't just about the perfect cut or color.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Time and again, clients would come to me not just for styling, but with real concerns: persistent scalp issues, unexplained hair thinning, damage that wouldn't heal, sensitivity that made every salon visit uncomfortable.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                These experiences led me to pursue advanced training in scalp therapy. I wanted to offer more than temporary fixes — I wanted to provide real, lasting solutions rooted in clinical knowledge and therapeutic care.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                The Philosophy:<br />Healthy Scalp,<br />Beautiful Hair
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                This is the core principle that guides everything I do: <strong>hair beauty starts from scalp health</strong>. You cannot have truly healthy, vibrant hair without a healthy scalp foundation.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Think of your scalp as the soil and your hair as the plant. No matter how much you water or fertilize a plant, if the soil is unhealthy, the plant will struggle. The same is true for hair.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                This philosophy transformed my approach. I don't just style hair — I assess scalp health, identify underlying issues, and create personalized treatment plans that address problems at their root cause.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Whether you're dealing with thinning, oiliness, sensitivity, or damage, my goal is to restore your scalp's natural balance so your hair can thrive.
              </p>
            </div>
            <div className="relative order-1 md:order-2">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=close%20up%20of%20healthy%20scalp%20examination%20with%20professional%20hands%2C%20scalp%20analysis%20and%20treatment%20concept%2C%20natural%20lighting%2C%20clean%20minimal%20aesthetic%2C%20premium%20hair%20care%2C%20soft%20focus%20background%2C%20warm%20neutral%20tones%2C%20professional%20beauty%20treatment%20setting%2C%20serene%20calming%20atmosphere%2C%20clinical%20expertise&width=600&height=750&seq=about-philosophy-01&orientation=portrait" 
                  alt="Scalp Health Philosophy" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dual Expertise Section */}
      <section className="py-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              A Rare Combination of Skills
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Most stylists focus on aesthetics. Most therapists focus on treatment. I bring both perspectives to every client.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-10">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-800 rounded-full mb-6">
                <i className="ri-scissors-cut-line text-3xl text-white"></i>
              </div>
              <h3 className="font-serif text-3xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                The Artist
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                With over 20 years of hands-on experience, I've mastered the art of cutting, coloring, and styling diverse hair types from around the world.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Precision cutting techniques for all hair textures</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Advanced color formulation and application</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Understanding of cultural hair preferences</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Expertise in European, Asian, and textured hair</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-10">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-800 rounded-full mb-6">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              <h3 className="font-serif text-3xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                The Therapist
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                My advanced scalp therapy certification allows me to diagnose and treat scalp conditions with clinical precision and therapeutic care.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Professional scalp analysis and diagnosis</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Targeted treatment protocols for specific conditions</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Understanding of scalp biology and health</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Holistic approach to hair and scalp wellness</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* International Experience Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Serving the<br />International<br />Community
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Hong Kong is one of the most diverse cities in the world, and my client base reflects that beautiful diversity. Over two decades, I've had the privilege of working with clients from over 30 countries.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                This international experience taught me that hair care is never one-size-fits-all. European fine hair requires different techniques than Asian thick hair. Textured curls need different products than straight hair. Cultural preferences vary widely.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                I've learned to listen carefully, understand individual needs, and adapt my approach to each person's unique hair type, lifestyle, and goals.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Whether you're an expat adjusting to Hong Kong's humid climate, a local seeking specialized care, or an international professional with specific styling needs, you'll receive personalized attention rooted in deep cross-cultural understanding.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=diverse%20international%20women%20with%20different%20hair%20types%20and%20textures%2C%20multicultural%20beauty%20concept%2C%20natural%20lighting%2C%20warm%20neutral%20tones%2C%20elegant%20minimal%20aesthetic%2C%20premium%20quiet%20luxury%20feel%2C%20professional%20salon%20setting%2C%20serene%20calming%20atmosphere%2C%20inclusive%20beauty%20representation&width=600&height=750&seq=about-international-01&orientation=portrait" 
                  alt="International Clientele" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Approach Section */}
      <section className="py-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            My Approach to Every Client
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-12">
            Every consultation begins with listening. I want to understand not just what you want, but what you've been experiencing, what hasn't worked, and what your daily life looks like.
          </p>

          <div className="grid md:grid-cols-3 gap-8 text-left">
            <div>
              <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-full mb-4">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Listen & Assess
              </h3>
              <p className="text-gray-600 leading-relaxed">
                I take time to understand your concerns, examine your scalp and hair health, and identify any underlying issues that need attention.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-full mb-4">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Create a Plan
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Based on my assessment, I develop a personalized treatment and styling plan that addresses your specific needs and goals.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-full mb-4">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Deliver Results
              </h3>
              <p className="text-gray-600 leading-relaxed">
                I combine therapeutic treatments with expert styling to give you not just beautiful hair, but healthy hair that lasts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Let's Start Your Journey<br />to Healthier Hair
          </h2>
          <p className="text-gray-600 text-lg mb-10 max-w-2xl mx-auto">
            Whether you're dealing with a specific concern or simply want expert care, I'm here to help. Book a consultation and let's create a personalized plan for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="https://wa.me/message/YAZZ2YL77SHLN1" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-3.5 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              WhatsApp Me
            </a>
            <Link 
              to="/contact"
              className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-3.5 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer"
            >
              Book Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://www.museshaircwb.com/wp-content/uploads/2024/01/cropped-Muses-Hair-Logo-1.png" 
                alt="Muses Hair CWB" 
                className="h-11 w-auto mb-6 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Professional solution center for hair and scalp problems in Causeway Bay, Hong Kong.
              </p>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">About</Link></li>
                <li><Link to="/qualifications" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Qualifications</Link></li>
                <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Services</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">More</h4>
              <ul className="space-y-3">
                <li><Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Products</Link></li>
                <li><Link to="/testimonials" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Testimonials</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">Connect</h4>
              <ul className="space-y-3">
                <li><a href="https://www.instagram.com/museshaircwb" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Instagram</a></li>
                <li><a href="https://www.facebook.com/museshaircwb" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Facebook</a></li>
                <li><a href="https://wa.me/message/YAZZ2YL77SHLN1" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">WhatsApp</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-400 text-sm">
              © 2024 Muses Hair CWB. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}